import { Badge } from "@freightsimple/ui-core/Display/Badge";
import { EmptyState } from "@freightsimple/ui-core/Display/EmptyState";
import { KeyValueList } from "@freightsimple/ui-core/Display/KeyValueList";
import { Panel } from "@freightsimple/ui-core/PageLayout/Panel";
import { Stack } from "@freightsimple/ui-core/ComponentLayout/Stack";
import { T } from "@freightsimple/ui-core/Typography/T";
import { Tab } from "@freightsimple/ui-core/PageLayout/Tab";
import { Table, type Column } from "@freightsimple/ui-core/Table/Table";
import { TextCell } from "@freightsimple/ui-core/Cells/TextCell";
import { ReceiptIcon } from "@phosphor-icons/react/Receipt";
import { type AuditChallenge } from "@freightsimple/generated-carriers-openapi-client/models/AuditChallenge";
import { type AuditProblemSummary } from "@freightsimple/generated-carriers-openapi-client/models/AuditProblemSummary";
import { type CarrierInvoiceLine } from "@freightsimple/generated-carriers-openapi-client/models/CarrierInvoiceLine";
import { type ShipmentInvoiceDetail } from "@freightsimple/generated-carriers-openapi-client/models/ShipmentInvoiceDetail";
import { formatMoney } from "@/App/Views/carrierFormatting";
import { emailBodyToText } from "@/App/Shipment/emailBodyToText";

const INVOICE_COLUMNS: Column<CarrierInvoiceLine>[] = [
  {
    key: "invoiceIdentifier",
    header: "Invoice",
    render: (line) => <TextCell value={line.invoiceIdentifier ?? undefined} />,
  },
  {
    key: "receivedAt",
    header: "Received",
    render: (line) => <TextCell value={line.receivedAt ?? undefined} />,
  },
  {
    key: "state",
    header: "Status",
    render: (line) => <TextCell value={line.state ?? undefined} />,
  },
  {
    key: "amountCad",
    header: "Amount",
    render: (line) => <TextCell value={formatMoney(line.amountCad)} />,
  },
];

/**
 * The money side of the shipment, and the case behind an audit.
 *
 * The evidence lives here rather than on a page of its own: an audit is a fact
 * about a shipment, and what settles it — the quote, the invoices, the paperwork
 * — is shipment context. A separate screen would show the same documents twice.
 */
export function ShipmentInvoiceTab({
  invoice,
}: {
  invoice: ShipmentInvoiceDetail | undefined;
}) {
  if (invoice === undefined) {
    return (
      <Tab id="invoice" title="Invoice">
        <EmptyState
          icon={ReceiptIcon}
          title="Loading invoice details…"
          description="What you invoiced, what we've approved, and anything we're querying."
        />
      </Tab>
    );
  }

  return (
    <Tab id="invoice" title="Invoice">
      <Stack gap={4}>
        <Panel title="The Money">
          <KeyValueList items={moneyItems_(invoice)} />
        </Panel>

        {invoice.invoices.length > 0 && (
          <Panel
            title="Your Invoices"
            fullBleed
            count={invoice.invoices.length}
          >
            <Table
              columns={INVOICE_COLUMNS}
              data={invoice.invoices}
              rowKey={(line) =>
                line.invoiceIdentifier ?? String(line.amountCad)
              }
            />
          </Panel>
        )}

        {invoice.problems.length > 0 && (
          <Panel
            title="What We're Querying"
            count={invoice.problems.length}
            description="Each item we've raised on this shipment, and whether it's settled."
          >
            <Stack gap={2}>
              {invoice.problems.map((problem) => (
                <ProblemRow_ key={problem.label} problem={problem} />
              ))}
            </Stack>
          </Panel>
        )}

        {invoice.challenges.length > 0 && (
          <Panel
            title="What We Sent You"
            count={invoice.challenges.length}
            description="The emails we've sent about this shipment, exactly as you received them."
            collapsible
          >
            <Stack gap={4}>
              {invoice.challenges.map((challenge) => (
                <ChallengeBlock_
                  key={challenge.challengeId}
                  challenge={challenge}
                />
              ))}
            </Stack>
          </Panel>
        )}

        {/* The request/response with the carrier's own system at quote time.
            Collapsed by default — it's long, and it only matters when the
            dispute is about what was quoted in the first place. */}
        {invoice.quoteRequests.length > 0 && (
          <Panel
            title="What Your System Quoted Us"
            count={invoice.quoteRequests.length}
            description="The rate request we sent your system when this shipment was quoted, and its reply."
            collapsible
            defaultOpen={false}
          >
            <Stack gap={4}>
              {invoice.quoteRequests.map((exchange) => (
                <Stack gap={1} key={exchange.exchangeId}>
                  <T size="sm" weight="medium">
                    {exchange.requestedAt ?? "At quote time"}
                  </T>
                  {exchange.response && (
                    <T size="sm" color="secondary" mono>
                      {exchange.response}
                    </T>
                  )}
                </Stack>
              ))}
            </Stack>
          </Panel>
        )}
      </Stack>
    </Tab>
  );
}

function moneyItems_(invoice: ShipmentInvoiceDetail) {
  const difference = invoice.differenceCad;
  return [
    {
      label: "We agreed to pay",
      value:
        invoice.quotedCad === undefined ? "—" : formatMoney(invoice.quotedCad),
    },
    {
      label: "You've invoiced",
      value:
        invoice.invoicedCad === undefined
          ? "No invoice received yet"
          : formatMoney(invoice.invoicedCad),
    },
    ...(difference === undefined || difference === 0
      ? []
      : [
          {
            label: "Difference",
            value:
              difference > 0
                ? `${formatMoney(difference)} above the quote`
                : `${formatMoney(-difference)} below the quote`,
          },
        ]),
    ...(invoice.quoteReference
      ? [{ label: "Your quote reference", value: invoice.quoteReference }]
      : []),
    ...(invoice.quotingMethod
      ? [{ label: "How we priced it", value: invoice.quotingMethod }]
      : []),
    ...(invoice.quoteExplanation
      ? [{ label: "How the rate was built", value: invoice.quoteExplanation }]
      : []),
  ];
}

function ProblemRow_({ problem }: { problem: AuditProblemSummary }) {
  return (
    <Stack gap={1}>
      <Badge
        label={problem.label}
        variant={problem.resolved ? "success" : "warning"}
      />
      {problem.resolved && (
        <T size="sm" color="secondary">
          Settled
        </T>
      )}
    </Stack>
  );
}

// The challenge as sent, verbatim. Showing a reworded version would put a
// second, subtly different statement of our position in front of them.
function ChallengeBlock_({ challenge }: { challenge: AuditChallenge }) {
  return (
    <Stack gap={1}>
      <T size="sm" weight="medium">
        {challenge.subject}
      </T>
      {(challenge.sentAt || challenge.sentBy) && (
        <T size="sm" color="muted">
          {[challenge.sentAt, challenge.sentBy].filter(Boolean).join(" · ")}
        </T>
      )}
      {/* The stored body is the HTML we emailed; rendered as a text node it
          would show the carrier literal <p> and <strong> tags. */}
      <T size="sm" color="secondary" multiline>
        {emailBodyToText(challenge.body)}
      </T>
    </Stack>
  );
}
